import { nextTick } from 'vue';
import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';

import store from '@/store/store';
import Dashboard from '@/views/Dashboard.vue';
import { RootActions } from '@/store/types/action-types';
import { LayoutEnum } from '@/bundles/App/enums';
import { Nullable } from '@/utils/types';
import { isChunkLoadError, recoverFromStaleBuild } from '@/bundles/App/helpers/staleBuildRecovery';
import MarketingRoutes from '@/router/routes/marketing-routes';
import ReportRoutes from '@/router/routes/report-routes';
import MarketingTemplatesRoutes from '@/router/routes/marketing-templates-routes';
import ClientRoutes from '@/router/routes/client-routes';
import {
  redirectHandler,
  runRouteGuards,
  shouldCancelPendingRequests,
} from '@/router/guards';
import { defineRouteMeta } from '@/router/helpers/defineRouteMeta';
import { endNavigationProgress, startNavigationProgress } from '@/bundles/App/helpers/navigationProgress';

import AccountLayout from '@/bundles/App/components/AccountLayout.vue';
import Welcome from '@/components/Welcome.vue';

// lazy routes
const ReportDetail = () => import('@/views/Reports/ReportDetail.vue')
const CompaniesMergeWindow = () => import('@/components/companies/CompaniesMergeWindow.vue')
const SavedSets = () => import('@/views/SavedSet/SavedSets.vue')
const PipelinesNew = () => import('@/views/Pipeline/PipelinesNew.vue')
const OccupiersNew = () => import('@/views/Occupier/OccupiersNew.vue')
const RequirementsNew = () => import('@/views/Requirement/RequirementsNew.vue')
const ProjectsNew = () => import('@/views/Project/ProjectsNew.vue')
const AdministrationRootView = () => import('@/bundles/Administration/components/RootView/AdministrationRootView.vue');
const Administration = () => import('@/components/account/administration.vue')
const CompaniesNew = () => import('@/views/Company/CompaniesNew.vue')
const Properties = () => import('@/views/Property/Properties.vue')

// Availabilities
const Availability = () => import('@/views/Availability/Availability.vue')
const Availabilities = () => import('@/views/Availability/Availabilities.vue')

const ProjectCard = () => import('@/views/Project/ProjectNew.vue')
const Occupier = () => import('@/views/Occupier/OccupierNew.vue')
const OccupierMergeView = () => import('@/bundles/Occupier/views/merge/OccupierMergeView.vue');
const Requirement = () => import('@/views/Requirement/RequirementNew.vue')
const Pipeline = () => import('@/views/Pipeline/PipelineNew.vue')
const Property = () => import('@/views/Property/Property.vue')
const PropertyMergeView = () => import('@/bundles/Property/views/merge/PropertyMergeView.vue');
const Company = () => import('@/views/Company/CompanyNew.vue')
const AccessDenied = () => import('@/components/AccessDenied.vue')

// new public functions
const PublicMailchimpAvailability = () => import(/* webpackChunkName: "availability-public" */ '@/views/Public/Availability.vue')
const PublicAvailability = () => import(/* webpackChunkName: "availability-public" */ '@/views/Public/Availability.vue')

// Contacts
const ContactsNew = () => import(/* webpackChunkName: "contacts" */ '@/views/Contact/ContactsNew.vue')
const Contact = () => import(/* webpackChunkName: "contact-record" */ '@/views/Contact/ContactNew.vue')
const ContactsMergeWindow = () => import(/* webpackChunkName: "import-contacts" */'@/components/contacts/ContactsMergeWindow.vue')

// Comparables
const ComparablesNew = () => import(/* webpackChunkName: "comparables" */ '@/views/Comparable/ComparablesNew.vue')
const Comparable = () => import(/* webpackChunkName: "comparable" */ '@/views/Comparable/ComparableNew.vue')

// Deals
const Deal = () => import('@/views/Deals/DealNew.vue')
const DealsNew = () => import('@/views/Deals/DealsNew.vue')
const CreateDeal = () => import('@/views/Deals/DealsCreate.vue')

// Spatial
const SpatialSearch = () => import('@/components/spatial/SpatialSearch.vue')

// Public Email Preferences

// User Settings
const UserSettingsRootViewNew = () => import('@/views/Account/RootViewNew.vue')
const UserProfile = () => import('@/components/account/ProfileInfoForm.vue')
const UserSecurity = () => import('@/views/Account/Security.vue')
const UserMain = () => import('@/bundles/Settings/views/Main.vue')
const UserApps = () => import('@/views/Account/Apps.vue')
const MagicLinkAuth = () => import('@/bundles/Auth/views/MagicLinkAuth.vue');
const ResetPassword = () => import('@/bundles/Auth/views/ResetPassword.vue');
const DashboardOrder = () => import('@/bundles/Settings/components/DashboardOrder.vue');
const NotificationsSettings = () => import('@/bundles/Settings/components/NotificationsSettings.vue');
const OpexSettings = () => import('@/bundles/Settings/components/OpexSettings.vue');
const VisualizationSettings = () => import('@/bundles/Settings/components/VisualizationSettings.vue');
const PermissionSettings = () => import('@/bundles/Settings/components/PermissionSettings.vue');
const FocusAreaSettings = () => import('@/bundles/Settings/components/FocusAreaSettings.vue');

// Members
const Members = () => import('@/views/Member/Members.vue')

// Marketing
const MarketingDashboard = () => import(/* webpackChunkName: "marketing" */ '@/views/Marketing/MarketingDashboard.vue')
const PressReleases = () => import(/* webpackChunkName: "marketing" */ '@/views/Marketing/PressReleases.vue');
const PressRelease = () => import(/* webpackChunkName: "marketing" */ '@/views/Marketing/PressRelease.vue')

const AccountProviderList = () => import(/* webpackChunkName: "account-provider" */ '@/bundles/AccountProvider/views/AccountProviderList.vue');

// OAuth
const OAuthAuthorize = () => import('@/bundles/OAuth/views/OAuthAuthorize.vue');

// account-routes
const MyAccounts = () => import(/* webpackChunkName: "account-routes" */ '@/bundles/Account/views/MyAccounts.vue');

const routes: RouteRecordRaw[] = [
  {
    path: '/pub/availability/:hash',
    component: PublicAvailability,
    meta: defineRouteMeta({
      title: 'Availability',
    }),
    props: true,
  },
  {
    path: '/pub/:provider/availability/:hash/:mcCampaign/:mcContact',
    component: PublicMailchimpAvailability,
    meta: defineRouteMeta({
      title: 'Availability',
    }),
    props: true,
  },
  {
    path: '/',
    name: 'welcome',
    component: Welcome,
    meta: defineRouteMeta({
      title: 'Welcome',
      requiresTokenAbsence: true
    }),
  },
  {
    path: '/signin',
    name: 'signin',
    component: Welcome,
    meta: defineRouteMeta({
      title: 'Sign In',
      requiresTokenAbsence: true
    }),
  },
  // the Vue CLI starter page left from the initial commit; kept as a redirect so stale links land on sign-in
  { path: '/signup', redirect: '/' },
  // the old post-logout page (unused since 2019); kept as a redirect so stale links land on sign-in
  { path: '/goodbye', redirect: '/' },
  // a never-wired 2020 email-preferences mock-up (no handler); kept as a redirect so stale links land on sign-in
  { path: '/preferences', redirect: '/' },
  {
    name: 'forgot-password',
    path: '/forgot-password',
    meta: defineRouteMeta({
      title: 'Forgot Password',
    }),
    component: MagicLinkAuth,
  },
  {
    name: 'reset-password',
    path: '/reset-password',
    meta: defineRouteMeta({
      title: 'Reset Password',
    }),
    component: ResetPassword,
  },
  {
    path: '/my-accounts',
    name: 'my-accounts',
    component: MyAccounts,
    meta: defineRouteMeta({
      title: 'My Accounts',
      requiresTokenOrTemporaryToken: true
    }),
  },
  {
    // the not-found page lives under the account (named '404'), so it has the app's sidebar
    // and top bar; this path stays for links and bookmarks that still point here. Signed out
    // there is no account: go to sign-in like any protected page (it used to build
    // /undefined/not-found and save that as the page to return to)
    path: '/404',
    redirect: () => {
      const accountId = (store.state.activeAccount as Nullable<{ id: number }>)?.id ?? (store.state.user as any)?.active_account?.id;

      return accountId ? `/${accountId}/not-found` : { name: 'signin' };
    },
  },
  {
    path: '/oauth/authorize',
    name: 'oauth-authorize',
    component: OAuthAuthorize,
    meta: defineRouteMeta({
      title: 'Authorize Application',
      requiresToken: true,
    }),
  },
  {
    path: '/status',
    name: 'system-status',
    component: () => import('@/bundles/SystemStatus/views/SystemStatusPage.vue'),
    meta: defineRouteMeta({ title: 'System Status' }),
  },
  // all routes without accountId should be defined above that
  {
    path: '/:accountId',
    component: AccountLayout,
    meta: defineRouteMeta({
      requiresToken: true
    }),
    children: [
      {
        path: '', // handling routes if they have no account id
        // Router 4 never matches an empty child that has no component, so its
        // beforeEnter never ran and `/3` rendered a blank layout. A redirect
        // record does match: `/3` alone opens that account's dashboard; a
        // non-numeric first segment is a page missing its account id.
        redirect: (to) => {
          if (!isNaN(Number(to.params.accountId))) {
            return { name: 'dashboard', params: { accountId: to.params.accountId } };
          }

          return `/${(store.state.activeAccount as Nullable<{ id: number }>)?.id}${to.fullPath}`;
        },
      },
      {
        path: 'dashboard',
        name: 'dashboard', // :key="$route.fullPath" removed
        component: Dashboard,
        meta: defineRouteMeta({
          appShell: true,
          title: 'Dashboard',
          topBarWelcome: true,
        }),
      },
      {
        path: 'pipelines-dashboard',
        name: 'pipelines-dashboard',
        component: () => import('@/bundles/Pipeline/views/PipelineDashboardPage.vue'),
        meta: defineRouteMeta({
          appShell: true,
          title: 'Pipeline Dashboard',
        }),
      },
      {
        path: 'deals-dashboard',
        name: 'deals-dashboard',
        component: () => import('@/bundles/Deals/views/DealsDashboardPage.vue'),
        meta: defineRouteMeta({
          appShell: true,
          title: 'Deals Dashboard',
        }),
      },
      {
        path: 'availabilities-dashboard',
        name: 'availabilities-dashboard',
        component: () => import('@/bundles/Availabilities/views/AvailabilitiesDashboardPage.vue'),
        meta: defineRouteMeta({
          appShell: true,
          title: 'Availabilities Dashboard',
        }),
      },
      {
        path: 'comparables-dashboard',
        name: 'comparables-dashboard',
        component: () => import('@/bundles/Comparables/views/ComparablesDashboardPage.vue'),
        meta: defineRouteMeta({
          appShell: true,
          title: 'Comparables Dashboard',
        }),
      },
      {
        path: 'client',
        component: () => import('@/bundles/App/layouts/AppClientLayout.vue'),
        meta: defineRouteMeta({
          title: 'Dashboard',
          layout: LayoutEnum.Blank, // AppClientLayout brings its own bars
          allowClientAccess: true,
        }),
        children: [
          ...ClientRoutes,
        ],
      },
      {
        path: 'not-found',
        name: '404', // :key="$route.fullPath" removed
        component: () => import('@/views/NotFoundView.vue'),
        meta: defineRouteMeta({
          appShell: true,
          title: 'Page Not Found',
        }),
      },
      {
        path: 'no-access',
        name: 'no-access',
        component: () => import('@/bundles/Account/views/NoAccessView.vue'),
        meta: defineRouteMeta({
          appShell: true,
          title: 'No Access',
        }),
      },
      {
        path: 'properties',
        name: 'properties', // :key="$route.fullPath" removed
        component: Properties,
        meta: defineRouteMeta({
          title: 'Properties',
        }),
      },
      {
        path: 'property/:id',
        name: 'property',
        component: Property,
        meta: defineRouteMeta({
          appShell: true,
          title: 'Property',
          topBarTitle: 'Property Record',
          topBarSource: 'property',
        }),
        props: true,
      },
      {
        path: 'property-merge/:id',
        name: 'property-merge',
        component: PropertyMergeView,
        meta: defineRouteMeta({
          title: 'Property Merge',
        }),
      },
      {
        path: 'contacts',
        name: 'contacts', // :key="$route.fullPath" removed
        component: ContactsNew,
        meta: defineRouteMeta({
          title: 'Contacts',
        }),
      },
      {
        path: 'availabilities',
        component: Availabilities,
        name: 'availabilities', // :key="$route.fullPath" removed
        meta: defineRouteMeta({
          title: 'Availabilities',
        }),
      },
      {
        name: 'availability', // :key="$route.fullPath" removed
        path: 'availability/:id',
        component: Availability,
        meta: defineRouteMeta({
          appShell: true,
          title: 'Availability',
          topBarTitle: 'Availability Record',
          topBarSource: 'availability',
        }),
      },
      {
        path: 'projects',
        name: 'projects', // :key="$route.fullPath" removed
        component: ProjectsNew,
        meta: defineRouteMeta({
          title: 'Projects',
        }),
      },
      {
        path: 'project/:id',
        name: 'project',
        component: ProjectCard,
        meta: defineRouteMeta({
          appShell: true,
          title: 'Project',
          topBarTitle: 'Presentation Record',
          topBarSource: 'project',
        }),
        props: true,
      },
      {
        path: 'comparables',
        component: ComparablesNew,
        name: 'comparables', // :key="$route.fullPath" removed
        meta: defineRouteMeta({
          title: 'Comparables',
        }),
      },
      {
        path: 'occupiers',
        component: OccupiersNew,
        name: 'occupiers', // :key="$route.fullPath" removed
        meta: defineRouteMeta({
          title: 'Occupiers',
        }),
      },
      {
        path: 'occupier/:id',
        props: true,
        name: 'occupier',
        meta: defineRouteMeta({
          appShell: true,
          title: 'Occupier',
          topBarTitle: 'Occupier Record',
          topBarSource: 'occupier',
        }),
        component: Occupier,
      },
      {
        path: 'occupier-merge/:id',
        name: 'occupier-merge',
        component: OccupierMergeView,
        meta: defineRouteMeta({
          title: 'Occupier Merge',
        }),
      },
      {
        path: 'requirements',
        name: 'requirements', // :key="$route.fullPath" removed
        component: RequirementsNew,
        meta: defineRouteMeta({
          title: 'Requirements',
        }),
      },
      {
        path: 'requirement/:id',
        props: true,
        name: 'requirement',
        component: Requirement,
        meta: defineRouteMeta({
          appShell: true,
          title: 'Requirement',
          topBarTitle: 'Requirement Record',
          topBarSource: 'requirement',
        }),
      },
      {
        path: 'pipelines',
        name: 'pipelines', // :key="$route.fullPath" removed
        component: PipelinesNew,
        meta: defineRouteMeta({
          title: 'Pipelines',
        }),
      },
      {
        path: 'pipeline/:id',
        component: Pipeline,
        name: 'pipeline',
        props: true,
        meta: defineRouteMeta({
          appShell: true,
          title: 'Pipeline',
          topBarTitle: 'Pipeline Record',
          topBarSource: 'pipeline',
        }),
      },
      {
        path: 'deals',
        component: DealsNew,
        name: 'deals', // :key="$route.fullPath" removed
        meta: defineRouteMeta({
          title: 'Deals',
        }),
      },
      {
        path: 'deal/:id',
        component: Deal,
        name: 'deal',
        meta: defineRouteMeta({
          appShell: true,
          title: 'Deal',
          topBarTitle: 'Deal Record',
          topBarSource: 'deal',
        }),
      },
      {
        path: 'deals-create',
        component: CreateDeal,
        name: 'deal-create',
        meta: defineRouteMeta({
          title: 'Deal Create',
          layout: LayoutEnum.Blank,
        }),
      },
      {
        path: 'spatial-gis',
        component: SpatialSearch,
        name: 'spatial-search', // :key="$route.fullPath" removed
        meta: defineRouteMeta({
          appShell: true,
          title: 'Spatial GIS',
        }),
      },
      {
        path: 'contact/:id',
        name: 'contact',
        component: Contact,
        props: true,
        meta: defineRouteMeta({
          appShell: true,
          title: 'Contact',
          topBarTitle: 'Contact Record',
          topBarSource: 'contact',
        }),
      },
      {
        path: 'contact/:id/merge',
        name: 'contact-merge',
        component: ContactsMergeWindow,
        props: true,
        meta: defineRouteMeta({
          title: 'Contacts Merge',
        }),
      },
      {
        path: 'companies',
        component: CompaniesNew,
        name: 'companies', // :key="$route.fullPath" removed
        props: true,
        meta: defineRouteMeta({
          title: 'Companies',
        }),
      },
      {
        path: 'company/:id',
        name: 'company',
        component: Company,
        props: true,
        meta: defineRouteMeta({
          appShell: true,
          title: 'Company',
          topBarTitle: 'Company Record',
          topBarSource: 'company',
        }),
      },
      {
        path: 'company/:id/merge',
        name: 'company-merge',
        component: CompaniesMergeWindow,
        props: true,
        meta: defineRouteMeta({
          title: 'Companies Merge',
        }),
      },
      {
        path: 'report/:id/system',
        name: 'ReportDetail',
        component: ReportDetail,
        props: true,
        meta: defineRouteMeta({
          appShell: true,
          title: 'Report Detail',
        }),
      },
      ...MarketingTemplatesRoutes,
      {
        path: 'administration',
        component: Administration,
        // AccountLayout keeps this shell and its top bar mounted across admin pages
        meta: defineRouteMeta({
          appShell: true,
          backTo: { name: 'administration-root-page', label: 'Admin Dashboard' },
        }),
        children: [
          {
            path: '',
            name: 'administration-root-page',
            meta: defineRouteMeta({
              title: 'Administration',
              topBarTitle: 'Account Administration',
              hideBack: true,
            }),
            component: AdministrationRootView,
          },
          {
            path: 'member/:id', // removed :key="$route.fullPath" for all children
            component: () => import('@/views/Member/MemberRoot.vue'),
            children: [
              {
                path: '',
                name: 'member-account', // :key="$route.fullPath" removed
                meta: defineRouteMeta({
                  title: 'Member Account',
                  requiresFinancialAdminOrAdminRole: true,
                }),
                component: () => import('@/views/Member/MemberNew.vue'),
              },
              {
                path: 'notes',
                name: 'member-notes', // :key="$route.fullPath" removed
                meta: defineRouteMeta({
                  title: 'Member Notes',
                  requiresFinancialAdminOrAdminRole: true,
                }),
                component: () => import('@/views/Member/MemberNotes.vue'),
              },
              {
                path: 'hr',
                name: 'member-hr', // :key="$route.fullPath" removed
                meta: defineRouteMeta({
                  title: 'Member HR',
                  requiresFinancialAdminOrAdminRole: true,
                }),
                component: () => import('@/views/Member/MemberHR.vue'),
              },
              {
                path: 'security',
                name: 'member-security', // :key="$route.fullPath" removed
                component: () => import('@/views/Member/MemberSecurity.vue'),
                meta: defineRouteMeta({
                  title: 'Member Security',
                  requiresSuperAdminRole: true
                }),
              },
              {
                path: 'company-details',
                name: 'member-company-details', // :key="$route.fullPath" removed
                meta: defineRouteMeta({
                  title: 'Member Company Details',
                  requiresFinancialAdminOrAdminRole: true,
                }),
                component: () => import('@/views/Member/MemberCompanyDetails.vue'),
              },
              {
                path: 'license-info',
                name: 'member-license-info', // :key="$route.fullPath" removed
                meta: defineRouteMeta({
                  title: 'Member License Info',
                  requiresFinancialAdminOrAdminRole: true,
                  // TODO sychdo: handle adminToken guard
                  // requiresAdminToken: true
                }),
                component: () => import('@/views/Member/MemberLicense.vue'),
                beforeEnter (to, from, next) {
                  if ((store.state.adminToken && (new Date().getTime() < (store.state.adminTokenExpiresTime ?? 0))) || to.query.showLogin) {
                    next()
                  } else {
                    next(`${to.path}?showLogin=true`)
                  }
                },
              },
              {
                path: 'compensation',
                name: 'member-compensation', // :key="$route.fullPath" removed
                component: () => import('@/views/Member/MemberCompensation.vue'),
                meta: defineRouteMeta({
                  title: 'Member Compensation',
                  requiresFinancialAdminRole: true,
                  // TODO sychdo: handle adminToken guard
                  // requiresAdminToken: true
                }),
                beforeEnter (to, from, next) {
                  if ((store.state.adminToken && (new Date().getTime() < (store.state.adminTokenExpiresTime ?? 0))) || to.query.showLogin) {
                    next()
                  } else {
                    next(`${to.path}?showLogin=true`)
                  }
                },
              },
              {
                path: 'documents',
                name: 'member-documents', // :key="$route.fullPath" removed
                meta: defineRouteMeta({
                  title: 'Member Documents',
                  requiresSuperAdminRole: true
                }),
                component: () => import('@/views/Member/MemberDocuments.vue'),
              },
              {
                path: 'login',
                name: 'member-login', // :key="$route.fullPath" removed
                meta: defineRouteMeta({
                  title: 'Member Login',
                  requiresSuperAdminRole: true,
                }),
                component: () => import('@/views/Member/MemberLogin.vue'),
              },
            ]
          },
          {
            path: 'marketing-details',
            name: 'marketing-root',
            meta: defineRouteMeta({
              title: 'Press Release Settings',
              requiresFinancialOrMarketingAdminRole: true,
            }),
            component: () => import('@/views/Marketing/CompanyDetails.vue'),
          },
          {
            path: 'marketing-directives',
            name: 'marketing-directives',
            meta: defineRouteMeta({
              title: 'Marketing Missions & Directives',
              requiresFinancialOrMarketingAdminRole: true,
            }),
            component: () => import('@/bundles/Marketing/views/MarketingMissionsAndDirectives.vue'),
          },
          {
            path: 'high-resolution-usage',
            name: 'high-resolution-usage',
            meta: defineRouteMeta({
              title: 'High Resolution Usage + Costs',
              requiresFinancialOrMarketingAdminRole: true,
            }),
            component: () => import('@/bundles/Map/views/HighResolutionUsageView.vue'),
          },
          {
            path: 'external-provider-list',
            name: 'account-provider-list',
            meta: defineRouteMeta({
              title: 'External Providers',
              requiresFinancialOrMarketingAdminRole: true,
            }),
            component: AccountProviderList,
          },
          {
            path: 'documents',
            name: 'documents', // :key="$route.fullPath" removed
            meta: defineRouteMeta({
              title: 'Marketing Documents',
              useFilterPanel: true,
            }),
            component: () => import('@/components/account/view/Documents.vue'),
          },
          {
            path: 'account-details',
            name: 'account-details',
            meta: defineRouteMeta({
              title: 'Company Details',
              requiresAdminRole: true,
            }),
            component: () => import('@/components/account/view/Details.vue'),
          },
          {
            path: 'settings',
            name: 'compensation-plans',
            meta: defineRouteMeta({
              title: 'Compensation Plans',
              requiresToken: true,
              requiresFinancialAdminRole: true,
            }),
            component: () => import('@/components/account/view/CompensationPlans.vue'),
          },
          {
            path: 'spatial-settings',
            meta: defineRouteMeta({
              title: 'Spatial and Map Settings',
              requiresAdminRole: true,
            }),
            name: 'administration-spatial-settings',
            component: () =>
              import('@/components/account/view/SpatialSettings.vue'),
          },
          {
            path: 'data-management',
            meta: defineRouteMeta({
              title: 'Data Management',
            }),
            component: () => import('@/bundles/Administration/components/DataManagement/DataManagementRoot.vue'),
            children: [
              {
                path: 'contacts',
                meta: defineRouteMeta({
                  title: 'Import Contacts',
                  requiresFinancialOrMarketingAdminRole: true,
                }),
                props: true,
                component: () => import('@/bundles/Administration/components/DataManagement/Contact/DataManagementCardContact.vue'),
                name: 'data-management-contacts',
              },
              {
                path: 'properties',
                meta: defineRouteMeta({
                  title: 'Import Properties',
                  requiresFinancialOrMarketingAdminRole: true,
                }),
                props: true,
                component: () => import('@/bundles/Administration/components/DataManagement/Property/DataManagementCardProperty.vue'),
                name: 'data-management-properties',
              },
              {
                path: 'availabilities',
                meta: defineRouteMeta({
                  title: 'Import Availabilities',
                  requiresFinancialOrMarketingAdminRole: true,
                }),
                props: true,
                component: () => import('@/bundles/Administration/components/DataManagement/Availability/DataManagementCardAvailability.vue'),
                name: 'data-management-availabilities',
              },
              {
                path: 'comparables',
                meta: defineRouteMeta({
                  title: 'Import Comparables',
                  requiresFinancialOrMarketingAdminRole: true,
                }),
                props: true,
                component: () => import('@/bundles/Administration/components/DataManagement/Comparable/DataManagementCardComparable.vue'),
                name: 'data-management-comparables',
              },
              {
                path: 'notes',
                meta: defineRouteMeta({
                  title: 'Import Notes',
                  requiresFinancialOrMarketingAdminRole: true,
                }),
                props: true,
                component: () => import('@/bundles/Administration/components/DataManagement/Note/DataManagementCardNote.vue'),
                name: 'data-management-notes',
              },
              {
                path: 'companies',
                meta: defineRouteMeta({
                  title: 'Import Companies',
                  requiresFinancialOrMarketingAdminRole: true,
                }),
                props: true,
                component: () => import('@/bundles/Administration/components/DataManagement/Company/DataManagementCardCompany.vue'),
                name: 'data-management-companies',
              },
              {
                path: 'tasks',
                meta: defineRouteMeta({
                  title: 'Import Tasks',
                  requiresFinancialOrMarketingAdminRole: true,
                }),
                props: true,
                component: () => import('@/bundles/Administration/components/DataManagement/Task/DataManagementCardTask.vue'),
                name: 'data-management-tasks',
              },
              {
                path: '',
                name: 'administration-data-management',
                meta: defineRouteMeta({
                  title: 'Data Management',
                }),
                props: true,
                component: () => import('@/bundles/Administration/components/DataManagement/DataManagementNew.vue'),
              },
              {
                path: 'contacts/:id',
                meta: defineRouteMeta({
                  title: 'Import Contacts',
                  requiresFinancialOrMarketingAdminRole: true,
                }),
                props: true,
                component: () => import('@/views/Administration/ProcessImport.vue'),
                name: 'data-management-contacts-process',
              },
              // saved sets import
              {
                path: 'contacts-set',
                meta: defineRouteMeta({
                  title: 'Import Contacts into Saved Set',
                  requiresFinancialOrMarketingAdminRole: true,
                }),
                props: true,
                component: () => import('@/bundles/Administration/components/DataManagement/Contact/DataManagementCardContactSavedSet.vue'),
                name: 'data-management-contacts-set',
              },

              // import list
              {
                path: 'imports',
                meta: defineRouteMeta({
                  title: 'Imports',
                  requiresFinancialOrMarketingAdminRole: true,
                }),
                props: true,
                component: () => import('@/bundles/Administration/components/DataManagement/ImportListPage.vue'),
                name: 'data-management-imports',
              },

              // lease ingestion
              {
                path: 'lease-ingestion',
                meta: defineRouteMeta({
                  title: 'Lease Ingestion',
                }),
                props: true,
                component: () => import('@/bundles/LeaseIngestion/components/LeaseIngestionQueuePage.vue'),
                name: 'data-management-lease-ingestion',
              },
              {
                path: 'lease-ingestion/:id',
                meta: defineRouteMeta({
                  title: 'Lease Ingestion',
                }),
                props: true,
                component: () => import('@/bundles/LeaseIngestion/components/LeaseIngestionDetailPage.vue'),
                name: 'data-management-lease-ingestion-detail',
              },

              // availability ingestion
              {
                path: 'availability-ingestion',
                meta: defineRouteMeta({
                  title: 'Availability Ingestion',
                }),
                props: true,
                component: () => import('@/bundles/AvailabilityIngestion/components/AvailabilityIngestionQueuePage.vue'),
                name: 'data-management-availability-ingestion',
              },
              {
                path: 'availability-ingestion/:id',
                meta: defineRouteMeta({
                  title: 'Availability Ingestion',
                }),
                props: true,
                component: () => import('@/bundles/AvailabilityIngestion/components/AvailabilityIngestionDetailPage.vue'),
                name: 'data-management-availability-ingestion-detail',
              },
            ]
          },
          {
            path: 'data-management/properties/:id',
            meta: defineRouteMeta({
              title: 'Import Properties',
              requiresFinancialOrMarketingAdminRole: true,
            }),
            props: true,
            component: () => import('@/bundles/Administration/components/DataManagement/ProcessPropertiesImport.vue'),
            name: 'data-management-properties-process',
          },
          {
            path: 'data-management/availabilities/:id',
            meta: defineRouteMeta({
              title: 'Import Availabilities',
              requiresFinancialOrMarketingAdminRole: true,
            }),
            props: true,
            component: () => import('@/bundles/Administration/components/DataManagement/ProcessAvailabilitiesImport.vue'),
            name: 'data-management-availabilities-process',
          },
          {
            path: 'data-management/comparables/:id',
            meta: defineRouteMeta({
              title: 'Import Comparables',
              requiresFinancialOrMarketingAdminRole: true,
            }),
            props: true,
            component: () => import('@/bundles/Administration/components/DataManagement/ProcessComparablesImport.vue'),
            name: 'data-management-comparables-process',
          },
          {
            path: 'data-management/notes/:id',
            meta: defineRouteMeta({
              title: 'Import Notes',
              requiresFinancialOrMarketingAdminRole: true,
            }),
            props: true,
            component: () => import('@/bundles/Administration/components/DataManagement/ProcessNotesImport.vue'),
            name: 'data-management-notes-process',
          },
          {
            path: 'data-management/companies/:id',
            meta: defineRouteMeta({
              title: 'Import Companies',
              requiresFinancialOrMarketingAdminRole: true,
            }),
            props: true,
            component: () => import('@/bundles/Administration/components/DataManagement/Company/ProcessCompaniesImport.vue'),
            name: 'data-management-companies-process',
          },
          {
            path: 'data-management/tasks/:id',
            meta: defineRouteMeta({
              title: 'Import Tasks',
              requiresFinancialOrMarketingAdminRole: true,
            }),
            props: true,
            component: () => import('@/bundles/Administration/components/DataManagement/Task/ProcessTasksImport.vue'),
            name: 'data-management-tasks-process',
          },
          {
            path: 'requests',
            meta: defineRouteMeta({
              title: 'Feature Requests and Bug Tracker'
            }),
            name: 'administration-requests', // :key="$route.fullPath" removed
            component: () => import('@/views/Administration/RequestsTracker.vue'),
          },
          {
            path: 'request/:id',
            meta: defineRouteMeta({
              title: 'Request',
              backTo: { name: 'administration-requests', label: 'Requests' },
            }),
            name: 'administration-request-record',
            component: () => import('@/bundles/Requests/views/RequestView.vue'),
          },

          {
            path: 'members',
            name: 'members', // :key="$route.fullPath" removed
            meta: defineRouteMeta({
              title: 'Members and Teams',
              hideMenuButton: true,
              requiresToken: true,
              requiresFinancialAdminOrAdminRole: true,
            }),
            component: Members
          },
          {
            path: 'account-settings',
            meta: defineRouteMeta({
              title: 'Settings',
              requiresAdminRole: true,
            }),
            name: 'administration-account-settings',
            component: () => import('@/views/Administration/AccountSettings.vue'),
          },
          {
            path: 'dynamic-data',
            meta: defineRouteMeta({
              title: 'Dynamic Data',
              requiresAdminRole: true,
            }),
            name: 'administration-dynamic-data',
            component: () => import('@/views/Administration/DynamicData.vue'),
          },
          {
            path: 'dealius-push',
            meta: defineRouteMeta({
              title: 'Dealius Push',
              requiresFinancialAdminRole: true,
            }),
            name: 'administration-dealius-push',
            component: () => import('@/views/Administration/DealiusPush.vue'),
          },
          {
            path: 'listing-verification',
            name: 'ListingVerificationSettings',
            meta: defineRouteMeta({
              title: 'Listing Verification Settings',
              requiresFinancialOrMarketingAdminRole: true,
            }),
            component: () => import('@/views/Administration/ListingVerification.vue'),
          },
          {
            path: 'mcp-roles',
            name: 'administration-mcp-roles',
            meta: defineRouteMeta({
              title: 'MCP Roles',
              requiresSuperAdminRole: true,
            }),
            component: () => import('@/bundles/Administration/components/McpRoles/McpRolesView.vue'),
          }
        ]
      },
      {
        path: 'saved-sets',
        name: 'sets', // :key="$route.fullPath" removed
        component: SavedSets,
        meta: defineRouteMeta({
          title: 'Saved Sets',
        }),
      },
      {
        path: 'sign-list',
        name: 'sign-list', // :key="$route.fullPath" removed
        component: () => import('@/bundles/SignIn/views/SignListView.vue'),
        props: true,
        meta: defineRouteMeta({
          title: 'Sign List',
        }),
      },
      {
        path: 'sign/:id',
        name: 'sign', // :key="$route.fullPath" removed
        component: () => import('@/bundles/SignIn/views/SignRecordView.vue'),
        props: true,
        meta: defineRouteMeta({
          appShell: true,
          title: 'Sign',
          topBarTitle: 'Sign Record',
          topBarSource: 'sign',
        }),
      },
      {
        path: 'order-approve/:id',
        name: 'order-approve',
        component: () => import('@/components/workorder/OrderApproval.vue'),
        props: true,
        meta: defineRouteMeta({
          appShell: true,
          title: 'Approve Sign Work Order',
        }),
      },
      {
        path: 'access',
        name: 'access-denied',
        component: AccessDenied,
        meta: defineRouteMeta({
          appShell: true,
          title: 'Access Denied',
        }),
      },
      {
        path: 'comparable/:id',
        name: 'comparable',
        component: Comparable,
        props: true,
        meta: defineRouteMeta({
          appShell: true,
          title: 'Comparable',
          topBarTitle: 'Comparable Record',
          topBarSource: 'comparable',
        }),
      },
      {
        component: UserSettingsRootViewNew,
        path: 'settings',
        // AccountLayout keeps the top bar mounted between the dashboard, admin and these pages
        meta: defineRouteMeta({
          appShell: true,
          topBarTitle: 'My Profile',
        }),
        children: [
          {
            path: '',
            name: 'user-profile', // :key="$route.fullPath" removed
            meta: defineRouteMeta({
              title: 'Profile',
              allowClientAccess: true,
            }),
            component: UserProfile,
          },
          {
            path: 'security',
            name: 'user-security', // :key="$route.fullPath" removed
            meta: defineRouteMeta({
              title: 'Security',
              allowClientAccess: true,
            }),
            component: UserSecurity,
          },
          {
            path: 'main',
            name: 'user-main', // :key="$route.fullPath" removed
            meta: defineRouteMeta({
              title: 'Main Settings',
            }),
            component: UserMain,
            // the tabs page has nothing of its own, so it opens on its first tab
            redirect: { name: 'settings-dashboard' },
            children: [
              {
                path: 'dashboard',
                name: 'settings-dashboard', // :key="$route.fullPath" removed
                meta: defineRouteMeta({
                  title: 'Dashboard Settings',
                }),
                component: DashboardOrder,
              },
              {
                path: 'notifications',
                name: 'settings-notifications', // :key="$route.fullPath" removed
                meta: defineRouteMeta({
                  title: 'Notifications Settings',
                }),
                component: NotificationsSettings,
              },
              {
                path: 'opex',
                name: 'settings-opex', // :key="$route.fullPath" removed
                meta: defineRouteMeta({
                  title: 'Opex Settings',
                }),
                component: OpexSettings,
              },
              {
                path: 'visualizations',
                name: 'settings-visualizations',
                meta: defineRouteMeta({
                  title: 'Visualizations Settings',
                }),
                component: VisualizationSettings,
              },
              {
                path: 'permissions-settings',
                name: 'permissions-settings',
                meta: defineRouteMeta({
                  title: 'Permission Settings',
                }),
                component: PermissionSettings,
              },
            ]
          },
          {
            path: 'apps',
            name: 'user-apps', // :key="$route.fullPath" removed
            component: UserApps,
            meta: defineRouteMeta({
              title: 'Apps',
            }),
            props: true,
          },
          {
            path: 'downloads',
            name: 'user-downloads', // :key="$route.fullPath" removed
            component: () => import('@/bundles/User/views/UserDownloadsView.vue'),
            meta: defineRouteMeta({
              title: 'My Downloads',
            }),
            props: true,
          },
          {
            path: 'exports',
            name: 'exports', // :key="$route.fullPath" removed
            component: () => import('@/bundles/Export/components/ExportListTable.vue'),
            meta: defineRouteMeta({
              title: 'My Exports',
            }),
            props: true,
          },
          {
            path: 'reports',
            name: 'reports', // :key="$route.fullPath" removed
            component: () => import('@/bundles/Reports/components/ReportListTable.vue'),
            meta: defineRouteMeta({
              title: 'My Reports',
            }),
            props: true,
          },
          {
            path: 'focus-area',
            name: 'settings-focus-area', // :key="$route.fullPath" removed
            meta: defineRouteMeta({
              title: 'Focus Area',
            }),
            component: FocusAreaSettings,
          },
          {
            path: 'ai-keys',
            name: 'settings-ai-keys', // :key="$route.fullPath" removed
            meta: defineRouteMeta({
              title: 'AI Keys',
            }),
            component: () => import('@/bundles/Settings/components/AiKeysSettings.vue'),
          }
        ],
      },
      {
        path: 'marketing',
        name: 'marketing', // :key="$route.fullPath" removed
        component: MarketingDashboard,
        props: true,
        meta: defineRouteMeta({
          appShell: true,
          title: 'Marketing',
        }),
      },
      {
        path: 'marketing/press-releases',
        name: 'PressReleases', // :key="$route.fullPath" removed
        component: PressReleases,
        meta: defineRouteMeta({
          title: 'Press Releases',
        }),
        props: true,
      },
      {
        name: 'PressRelease',
        path: 'marketing/press-release/:id',
        component: PressRelease,
        props: true,
        meta: defineRouteMeta({
          appShell: true,
          title: 'Press Release',
          topBarTitle: 'Press Release Record',
          topBarSource: 'pressrelease',
        }),
      },
      // The approval request email / notification / reminder links land here.
      // The press release record shows the approve, deny and request-changes
      // actions for the signed-in broker's own pending request, so the link only
      // has to open that record.
      {
        name: 'ProcessPressRelease',
        path: 'marketing/press-release/:id/approve/:approveKey',
        redirect: to => ({ name: 'PressRelease', params: { accountId: to.params.accountId, id: to.params.id } }),
      },
      ...MarketingRoutes,
      ...ReportRoutes,
      {
        // Vue Router 4 dropped `path: '*'`; the named catch-all is the replacement, and a
        // record without a component is discarded, so the 404 view is attached here too.
        path: ':pathMatch(.*)*', // handling all not existing routes and routes with id in url
        component: () => import('@/views/NotFoundView.vue'),
        meta: defineRouteMeta({
          appShell: true,
          title: 'Page Not Found',
        }),
        beforeEnter: (to, from, next) => {
          redirectHandler(to, next, store, true);
        }
      },
    ],
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior (to, from, savedPosition) {
    // A query/hash-only change (same path) must not jump the page to the top.
    if (to.path === from.path) {
      return false
    }
    return { left: 0, top: 0 }
  }
})

// a route's lazy chunk is gone after a deploy (stale service worker): load the target page afresh
router.onError((error, to) => {
  endNavigationProgress();
  if (isChunkLoadError(error)) {
    recoverFromStaleBuild(to.fullPath);
  }
})

export const DEFAULT_TITLE = 'Slick Cactus';

// the top loading bar: on for every navigation, shown only if it takes a while (lazy page chunk)
router.beforeEach(() => {
  startNavigationProgress();
});

router.beforeEach((to, from, next) => {
  if (window.location.hash) { // handle hash-to-history redirect before any guards e.g. /#/3/occupiers -> /3/occupiers
    const pathWithoutHash = window.location.hash.slice(1); // Extract the hash path (without the '#')

    if (pathWithoutHash && pathWithoutHash !== '/') { // Check if there's an actual path after the hash
      // clear the hash from the browser URL before calling next().
      // without this, window.location.hash still contains the old value. when next() synchronously triggers the next beforeEach cycle, causing an infinite redirect loop.
      window.history.replaceState(null, '', pathWithoutHash);
      next({ path: pathWithoutHash, replace: true });
      return;
    }
  }

  if (runRouteGuards(to, from, next, store)) {
    if (shouldCancelPendingRequests(to, from)) {
      store.dispatch(RootActions.CANCEL_PENDING_REQUESTS);
    }
    next();
  }
})

router.afterEach((to, from, failure) => {
  endNavigationProgress();

  // Vue Router 4 also runs afterEach for a navigation a guard aborted (the
  // merge pages' leave confirmation); Vue Router 3 did not. The title must
  // keep naming the page the user is still on.
  if (failure) return;

  // A query-only change (list filters, a dashboard's date range) stays on the same page, which may
  // have set its own title (the record's name); only a new path gets the route's title.
  if (to.path === from.path) return;

  // this is a hack to fix the issue with the title not being updated with history mode (need to have it in future)
  // see: https://github.com/vuejs/vue-router/issues/914#issuecomment-384477609
  nextTick(() => {
    document.title = to.meta.title ? `${to.meta.title} - ${DEFAULT_TITLE}` : DEFAULT_TITLE;
  });
});

export default router
